import { useState, useEffect, useCallback } from "react";
import { db, storage } from "../firebaseConfig";
import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { sanitizeImage, MAX_UPLOAD_FILES } from "../utils/safeImageUpload";
import { useAuth } from "../context/AuthContext";
import { grantTesterPremium } from "../services/testerService";

/**
 * Hook for submitting and viewing tester feedback.
 */
export default function useTesterFeedback() {
  const { currentUser, client, refreshClient } = useAuth();
  const [pastFeedback, setPastFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const uid = currentUser?.uid;

  // Real-time listener for current user's tester feedback
  useEffect(() => {
    if (!uid) {
      setPastFeedback([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "feedback"),
      where("uid", "==", uid),
      where("isTesterFeedback", "==", true),
      orderBy("dateSubmitted", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setPastFeedback(
          snapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
        setLoading(false);
      },
      (err) => {
        console.error("Tester feedback listener error:", err);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [uid]);

  /**
   * Upload screenshots to Firebase Storage.
   */
  const uploadScreenshots = useCallback(
    async (files) => {
      if (!files || files.length === 0) return [];

      if (files.length > MAX_UPLOAD_FILES) throw new Error(`Please attach up to ${MAX_UPLOAD_FILES} screenshots.`);
      // Security checks: real image types only, size limits, re-encoded to strip hidden content
      const clean = await Promise.all(Array.from(files).map(sanitizeImage));
      const uploaded = [];
      for (const file of clean) {
        const timestamp = Date.now();
        const safeName = file.name;
        const storagePath = `tester-feedback/${uid}/${timestamp}_${safeName}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file, { contentType: file.type });
        const url = await getDownloadURL(storageRef);
        uploaded.push({
          url,
          storagePath,
          fileName: file.name,
          uploadedAt: new Date().toISOString(),
        });
      }
      return uploaded;
    },
    [uid]
  );

  /**
   * Submit feedback to the feedback collection.
   */
  const submitFeedback = useCallback(
    async (data) => {
      if (!uid || submitting) return;
      setSubmitting(true);

      try {
        // Upload screenshots if any
        const screenshots = data.screenshotFiles
          ? await uploadScreenshots(data.screenshotFiles)
          : data.screenshots || [];

        const feedbackDoc = {
          uid,
          submittedBy: currentUser?.email || "unknown",
          submittedByName: client?.fullName || client?.name || "Customer",
          isTesterFeedback: client?.testerMode === true || data.courseReview === true,
          courseReview: data.courseReview === true,
          feedbackType: data.feedbackType || "overall",
          message: data.message || "",
          page: data.page || window.location.pathname,
          pageTitle: data.pageTitle || "",
          rating: data.rating || null,
          npsScore: data.npsScore ?? null,
          severity: data.severity || null,
          stepsToReproduce: data.stepsToReproduce || null,
          deviceInfo: data.deviceInfo || `${navigator.userAgent}`,
          screenshots,
          pageTags: data.pageTags || null,
          status: "open",
          dateSubmitted: serverTimestamp(),
          bugStatus: null,
          bugStatusUpdatedAt: null,
        };

        await addDoc(collection(db, "feedback"), feedbackDoc);

        // Increment tester feedback count and potentially grant premium
        if (client?.testerMode) {
          const currentCount = client?.testerFeedbackCount || 0;
          await grantTesterPremium(uid, currentCount);
        }

        // Refresh client data to update gates
        if (refreshClient) await refreshClient();

        return true;
      } catch (err) {
        console.error("Failed to submit tester feedback:", err);
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [uid, currentUser?.email, client, submitting, uploadScreenshots, refreshClient]
  );

  /**
   * Update bug status (still-happening or fixed).
   */
  const updateBugStatus = useCallback(async (feedbackId, status) => {
    const feedbackRef = doc(db, "feedback", feedbackId);
    await updateDoc(feedbackRef, {
      bugStatus: status,
      bugStatusUpdatedAt: serverTimestamp(),
    });
  }, []);

  return {
    pastFeedback,
    loading,
    submitting,
    submitFeedback,
    updateBugStatus,
  };
}
